import moment from "moment";
import { PaidItem } from "@/hooks/usePaidItems";

moment.locale('pt-BR');

const createSafeDate = (dateInput: Date | string | null | undefined): moment.Moment | null => {
    if (!dateInput) return null;
    
    if (dateInput instanceof Date) {
        return moment(dateInput);
    }
    
    const dateStr = String(dateInput);
    
    let parsedDate = moment(dateStr, 'YYYY-MM-DDTHH:mm:ss', true);
    if (parsedDate.isValid()) {
        return parsedDate;
    }
    
    parsedDate = moment(dateStr, 'YYYY-MM-DDTHH:mm', true);
    if (parsedDate.isValid()) {
        return parsedDate;
    }
    
    parsedDate = moment(dateStr, 'YYYY-MM-DD', true);
    if (parsedDate.isValid()) {
        return parsedDate;
    }
    
    parsedDate = moment(dateStr, 'YYYY-DD-MM', true);
    if (parsedDate.isValid()) {
        return parsedDate;
    }
    
    parsedDate = moment(dateStr);
    if (parsedDate.isValid()) {
        return parsedDate;
    }
    
    return null;
};

export interface Debts {
    sqPassagemAvulsa: number;
    dataPassagem: Date;
    idPassagemFreeFlow: number;
    valorPassagem: number;
    vlMulta: number;
    nmPraca: string;
    nmConcessionaria: string;
    vlTotalPagar: number;
    dtVencimento: Date;
    placa: string;
    fmtDataPassagem?: string;
    flRetentativa?: number; // 0-Não há retentativa pendente, 1-Existe retentativa pendente
}

export interface FormattedDebts {
    id: string;
    date: string;
    hour: string;
    price: string;
    originalPrice: string;
    dueDate: string;
    daysLeft: number;
    original: Debts;
    hasDate: boolean;
    plate: string;
    fmtDataPassagem?: string;
    nmConcessionaria: string;
    dtVencimento: string;
    flRetentativa: number; // 0-Não há retentativa pendente, 1-Existe retentativa pendente
}

const getSortedDebts: (debts: Debts[]) => Debts[] = (debts) => {
    return debts?.sort((a, b) => {
        const dateA = moment(a?.dtVencimento);
        const dateB = moment(b?.dtVencimento);
        return dateA.diff(dateB);
    });
}

export const getFormattedDebts: (debts: Debts[]) => FormattedDebts[] = (debts) => {
    const now = moment();
    
    return getSortedDebts(debts)?.map(item => {
        const hasDate = !!item?.dtVencimento;

        const date = moment(item?.dataPassagem);
        const dueDate = moment(item?.dtVencimento);

        const formattedDate = date.format('DD/MM/YYYY HH:mm:ss');
        const formattedHour = date.format('HH:mm:ss');
        const daysLeft = now.diff(dueDate, 'days');

        const price = item?.vlTotalPagar?.toFixed(2).replace(/\./g, ',');
        const originalPrice = item?.valorPassagem?.toFixed(2).replace(/\./g, ',');

        return {
            id: item?.sqPassagemAvulsa?.toString(),
            date: formattedDate,
            hour: formattedHour,
            dueDate: dueDate.format('DD/MM/YYYY'),
            daysLeft,
            price,
            originalPrice,
            original: item,
            hasDate,
            plate: item.placa,
            fmtDataPassagem: item?.fmtDataPassagem,
            nmConcessionaria: item.nmConcessionaria,
            dtVencimento: dueDate.format('DD/MM/YYYY'),
            flRetentativa: item.flRetentativa || 0,
        };
    }) || [];
};

export interface Tickets {
    sqPagamento: number;
    dataPagamento: string | null;
    valorPassagem: number;
    status: string;
    placaVeiculo: string;
    cdGrupo?: string;
    fmtDataPagamento?: string | null;
}

export interface FormattedTickets {
    id: string;
    date: string;
    price: string;
    status: string;
    plate: string;
    fullDate: Date | null;
    nmConcessionaria?: string;
    nmPraca?: string;
    cdGrupo?: string;
}

const getRemovedDuplicatesById: (tickets: Tickets[]) => Tickets[] = (tickets) => {
  const seen = new Set();
  return tickets.filter(item => {
    if (seen.has(item.sqPagamento)) return false;
    seen.add(item.sqPagamento);
    return true;
  });
}

const getSortedTickets: (tickets: Tickets[]) => Tickets[] = (tickets) => {
    return tickets?.sort((a, b) => {
        const dateA = a?.dataPagamento ? moment(a.dataPagamento) : moment(0);
        const dateB = b?.dataPagamento ? moment(b.dataPagamento) : moment(0);
        return dateB.diff(dateA);
    });
}

export const getFormattedTickets = (tickets: Tickets[]) => {

    if (!tickets) {
        return [];
    }

    const filteredTickets = tickets?.filter(item => {
        const status = item?.status?.toLowerCase() || '';
        const isValidStatus = status.includes('processamento') || 
                            status.includes('finalizado') || 
                            status.includes('aprovado') ||
                            status.includes('pago');
        
        return isValidStatus;
    });
    

    const unifiedTickets = getRemovedDuplicatesById(filteredTickets);

    const sorted = getSortedTickets(unifiedTickets);

    const formattedTickets: FormattedTickets[] = sorted?.map(item => {
        const date = createSafeDate(item?.dataPagamento);
        const formattedDate = date?.format('DD/MM/YYYY') || item?.fmtDataPagamento || "";
        
        const formattedTicket = {
            id: String(item?.sqPagamento ?? 0),
            date: formattedDate,
            fullDate: date?.toDate() ?? null,
            price: item?.valorPassagem?.toFixed(2)?.replace(/\./g, ','),
            status: item.status,
            plate: item.placaVeiculo,
            cdGrupo: item.cdGrupo,
            nmConcessionaria: item.cdGrupo, // Usando cdGrupo como fallback já que não vem nmConcessionaria
            nmPraca: undefined // Não vem no retorno
        };
        
        return formattedTicket;
    });

    return formattedTickets;
};

export interface DebtsByInvoice {
    sqPagamento: number;
    valorPassagem: number;
    dataTransacao: string;
    nmConcessionaria: string;
    nmPraca: string;
    placa: string;
    vlTotalPagar: number;
    valorMulta: number;
    fmtDataTransacao: string;
}

interface FormattedDebtsByInvoice {
    plate: string;
    date: string;
    price: string;
    owner: string;
    originalPrice: string;
    fine: string;
    nmPraca: string;
    fmtDataTransacao: string;
}

export const getFormattedDebtsByInvoice = (debts: DebtsByInvoice[]) => {
    if (!debts) return [];

    const formattedTickets: FormattedDebtsByInvoice[] = debts?.map(item => {
        const date = createSafeDate(item?.dataTransacao);
        const formattedDate = date?.format('DD/MM/YYYY');
        return {
            id: String(item?.sqPagamento ?? 0),
            date: formattedDate ?? "",
            price: item?.vlTotalPagar?.toFixed(2)?.replace(/\./g, ','),
            plate: item.placa,
            owner: item.nmConcessionaria,
            nmPraca: item.nmPraca,
            fmtDataTransacao: item.fmtDataTransacao,
            originalPrice: item?.valorPassagem?.toFixed(2)?.replace(/\./g, ','),
            fine: item?.valorMulta?.toFixed(2)?.replace(/\./g, ','),
        }
    });

    return formattedTickets;
};

interface FormattedPaidItem {
    id: string;
    date: string;
    price: string;
    status: string;
    plate: string;
    nmPraca: string;
    nmConcessionaria: string;
}

const getRemovedDuplicatesPaidItemsById: (items: PaidItem[]) => PaidItem[] = (items) => {
    const seen = new Set();
    return items.filter(item => {
        if (seen.has(item.idPassagemFreeFlow)) return false;
        seen.add(item.idPassagemFreeFlow);
        return true;
    });
}

const getSortedPaidItems: (items: PaidItem[]) => PaidItem[] = (items) => {
    const sortedItems = items?.sort((a, b) => {
        const dateA = moment(a?.dtPagamento);
        const dateB = moment(b?.dtPagamento);
        return dateB.diff(dateA);
    });
    
    return sortedItems;
}

export const getFormattedPaidItems = (items: PaidItem[]) => {
    if (!items) return [];

    const filteredItems = items?.filter(item => {
        const status = item?.statusTransacao?.toLowerCase() || '';
        const isValidStatus = status.includes('processamento') || 
                            status.includes('finalizado') || 
                            status.includes('aprovado') ||
                            status.includes('pago');
        
        return isValidStatus;
    });

    const unifiedItems = getRemovedDuplicatesPaidItemsById(filteredItems);

    const sorted = getSortedPaidItems(unifiedItems);

    const formattedItems: FormattedPaidItem[] = sorted?.map(item => {
        return {
            id: String(item?.idPassagemFreeFlow ?? 0),
            date: item.fmtDataPassagem,
            price: item?.vlTotalPagar?.toFixed(2)?.replace(/\./g, ','),
            status: item.statusTransacao,
            plate: item.placaVeiculo,
            nmPraca: item.nmPraca,
            nmConcessionaria: item.nmConcessionaria
        }
    });

    return formattedItems;
};

export const getMaskedCardNumber = (cardNumber: string): string => {
    const cleanedNumber = cardNumber.replace(/\D/g, '');
    if (cleanedNumber === "" || cleanedNumber.length < 4) return `**** **** **** ****`;
    const lastFourDigits = cleanedNumber.slice(-4);
    return `**** **** **** ${lastFourDigits}`;
}